import { publicChecklistErrorMessage } from '@/utils/publicChecklistErrorMessage';
import { isExpectedWebChecklistShareResolveError } from '@/utils/isExpectedWebChecklistShareResolveError';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  AppState,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from '@/config/theme';
import ChecklistDateTimeField from '@/components/checklist/ChecklistDateTimeField';
import { nextPublicChecklistPatchBatch } from '@/utils/publicChecklistPatchBatch';
import { ChecklistServiceLogger } from '@/utils/logger';
import { showToast } from '@/utils/toast';
import {
  ChecklistShareField,
  ChecklistShareLinkStatus,
  completeChecklistShareByToken,
  patchChecklistShareValues,
  resolveChecklistShareToken,
} from '@/services/checklistShareService';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';

type PublicChecklistGroup = {
  title: string;
  fields: ChecklistShareField[];
};

type ShareViewState = {
  shareLinkId: string;
  status: ChecklistShareLinkStatus;
  expiresAt: string;
  updatesCount: number;
  maxUpdates: number | null;
  lastActivityAt: string | null;
};

const publicLogger = ChecklistServiceLogger.Main;
const PATCH_DEBOUNCE_MS = 900;
const STATUS_REFRESH_MS = 15000;
const MIN_BOTTOM_ACTION_HEIGHT = 88;

const getSecondsRemaining = (expiresAtIso: string, nowMs: number): number => {
  const expiresAtMs = new Date(expiresAtIso).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return 0;
  }
  return Math.max(0, Math.floor((expiresAtMs - nowMs) / 1000));
};

const formatCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return 'Sem atividade';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Data inválida';
  }

  const now = new Date();
  const isSameDay =
    parsed.getDate() === now.getDate() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getFullYear() === now.getFullYear();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    parsed.getDate() === yesterday.getDate() &&
    parsed.getMonth() === yesterday.getMonth() &&
    parsed.getFullYear() === yesterday.getFullYear();

  const time = parsed.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isSameDay) {
    return `Hoje, ${time}`;
  }

  if (isYesterday) {
    return `Ontem, ${time}`;
  }

  const shortDate = parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });

  return `${shortDate} ${time}`;
};

const getStatusLabel = (status: ChecklistShareLinkStatus): string => {
  if (status === 'active') return 'Ativo';
  if (status === 'revoked') return 'Revogado';
  if (status === 'expired') return 'Expirado';
  return 'Concluído';
};

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  return 'Erro inesperado';
};

const extractTerminalStatusFromError = (error: unknown): ChecklistShareLinkStatus | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const maybeStatus =
    (error as { shareStatus?: unknown }).shareStatus ??
    (error as { status?: unknown }).status;

  if (maybeStatus === 'revoked' || maybeStatus === 'expired' || maybeStatus === 'completed') {
    return maybeStatus;
  }

  return null;
};

const isSessionErrorTerminal = (message: string): ChecklistShareLinkStatus | null => {
  const normalized = message.toLowerCase();
  // Evita falso positivo de "completed" em mensagens genéricas como
  // "Falha ao concluir sessão..." sem confirmação de status vinda da API.
  if (
    normalized.includes('(completed)') ||
    normalized.includes('status=completed') ||
    normalized.includes('"status":"completed"')
  ) {
    return 'completed';
  }
  if (
    normalized.includes('(revoked)') ||
    normalized.includes('status=revoked') ||
    normalized.includes('"status":"revoked"')
  ) {
    return 'revoked';
  }
  if (
    normalized.includes('(expired)') ||
    normalized.includes('status=expired') ||
    normalized.includes('"status":"expired"')
  ) {
    return 'expired';
  }
  if (normalized.includes('expir')) {
    return 'expired';
  }
  if (normalized.includes('revogad') || normalized.includes('não está ativo')) {
    return 'revoked';
  }
  if (normalized.includes('not active')) {
    return 'revoked';
  }
  return null;
};

const groupFields = (fields: ChecklistShareField[]): PublicChecklistGroup[] => {
  const groupsMap = new Map<string, ChecklistShareField[]>();

  fields.forEach((field) => {
    if (!groupsMap.has(field.grupo)) {
      groupsMap.set(field.grupo, []);
    }
    groupsMap.get(field.grupo)!.push(field);
  });

  return Array.from(groupsMap.entries()).map(([title, groupedFields]) => ({
    title,
    fields: groupedFields.sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0)),
  }));
};

const PublicChecklistPage = () => {
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const safeAreaInsets = useSafeAreaInsets();
  const token = useMemo(
    () => (Array.isArray(params.token) ? params.token[0] : params.token) || '',
    [params.token]
  );

  const [initialLoading, setInitialLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionState, setSessionState] = useState<ShareViewState | null>(null);
  const [checklistInfo, setChecklistInfo] = useState<{
    id: string;
    status: string;
    code: string;
    companyName: string;
  }>({
    id: '',
    status: '',
    code: '',
    companyName: '',
  });
  const [fields, setFields] = useState<ChecklistShareField[]>([]);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const [editableFieldIds, setEditableFieldIds] = useState<Set<string>>(new Set<string>());
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, string[]>>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [patchError, setPatchError] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [bottomActionHeight, setBottomActionHeight] = useState(
    MIN_BOTTOM_ACTION_HEIGHT + Math.max(safeAreaInsets.bottom, theme.spacing.sm)
  );

  const pendingChangesRef = useRef<Record<string, string | null>>({});
  const flushPromiseRef = useRef<Promise<boolean> | null>(null);
  const completingRef = useRef(false);
  const lastPatchFinishedAtRef = useRef(0);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const effectiveStatusRef = useRef<ChecklistShareLinkStatus | null>(null);

  const effectiveStatus: ChecklistShareLinkStatus | null = useMemo(() => {
    if (!sessionState) {
      return null;
    }
    if (sessionState.status !== 'active') {
      return sessionState.status;
    }
    const remaining = getSecondsRemaining(sessionState.expiresAt, nowTick);
    return remaining > 0 ? 'active' : 'expired';
  }, [nowTick, sessionState]);

  const isEditable = effectiveStatus === 'active';
  const secondsRemaining = useMemo(() => {
    if (!sessionState || effectiveStatus !== 'active') {
      return 0;
    }
    return getSecondsRemaining(sessionState.expiresAt, nowTick);
  }, [effectiveStatus, nowTick, sessionState]);

  const groupedFields = useMemo(() => groupFields(fields), [fields]);

  useEffect(() => {
    effectiveStatusRef.current = effectiveStatus;
  }, [effectiveStatus]);

  const clearFlushTimer = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const resolveSession = useCallback(
    async (options?: { preserveForm?: boolean; silent?: boolean }) => {
      if (!token) {
        setErrorMessage('Token do checklist não foi informado.');
        setInitialLoading(false);
        return;
      }

      if (!options?.silent) {
        setResolving(true);
      }

      try {
        publicLogger.info('[public-checklist] resolve init', {
          tokenLength: token.length,
          preserveForm: !!options?.preserveForm,
        });
        const payload = await resolveChecklistShareToken(token);

        if (!mountedRef.current) {
          return;
        }

        const code =
          payload.checklist.sequencial_codigo?.trim() ||
          (payload.checklist.sequencial_numero ? String(payload.checklist.sequencial_numero) : 'Sem numeração');

        setChecklistInfo({
          id: payload.checklist.id,
          status: payload.checklist.status,
          code,
          companyName: payload.empresa?.nome?.trim() || '',
        });

        setSessionState({
          shareLinkId: payload.share_link.id,
          status: payload.share_link.status,
          expiresAt: payload.share_link.expires_at,
          updatesCount: payload.share_link.updates_count,
          maxUpdates: payload.share_link.max_updates,
          lastActivityAt: payload.share_link.last_activity_at,
        });

        setFields(payload.fields || []);
        setEditableFieldIds(new Set(payload.editable_field_ids || []));
        setDropdownOptions(payload.dropdown_options || {});

        if (!options?.preserveForm) {
          const normalizedValues = Object.fromEntries(
            Object.entries(payload.values || {}).map(([fieldId, rawValue]) => [
              fieldId,
              rawValue === null || rawValue === undefined ? '' : String(rawValue),
            ])
          );
          setFormValues(normalizedValues);
        }

        setErrorMessage(null);
        publicLogger.info('[public-checklist] resolve success', {
          shareLinkId: payload.share_link.id,
          fieldsCount: payload.fields?.length || 0,
          editableCount: payload.editable_field_ids?.length || 0,
        });
      } catch (error) {
        const message = normalizeErrorMessage(error);
        if (!mountedRef.current) {
          return;
        }

        setErrorMessage(message);
        // Prioriza status técnico retornado pela Edge Function; fallback por parsing de mensagem.
        const terminalStatus = extractTerminalStatusFromError(error) || isSessionErrorTerminal(message);
        if (terminalStatus) {
          setSessionState((prev) =>
            prev
              ? {
                  ...prev,
                  status: terminalStatus,
                }
              : prev
          );
        }

        if (!isExpectedWebChecklistShareResolveError(error)) {
          captureSentryExceptionWithScope(error, {
            level: options?.silent ? 'warning' : 'error',
            context: {
              flow: 'public_checklist',
              stage: options?.silent ? 'resolve_silent' : 'resolve',
              tokenLength: token.length,
            },
            tags: {
              flow: 'public_checklist',
              stage: options?.silent ? 'resolve_silent' : 'resolve',
            },
            dedupeKey: options?.silent
              ? `public_checklist:resolve_silent:${token.length}`
              : `public_checklist:resolve:${message}`,
            dedupeWindowMs: options?.silent ? 45000 : 20000,
          });
        }
      } finally {
        if (!options?.silent) {
          setResolving(false);
        }
        setInitialLoading(false);
      }
    },
    [token]
  );

  const flushPendingChanges = useCallback((): Promise<boolean> => {
    if (flushPromiseRef.current) return flushPromiseRef.current;
    clearFlushTimer();
    const run = async (): Promise<boolean> => {
      setSyncing(true);
      let transientRetries = 0;
      let changedFieldIds: string[] = [];
      try {
        while (Object.keys(pendingChangesRef.current).length > 0) {
          if (!mountedRef.current || !token || effectiveStatusRef.current !== 'active') return false;
          // O intervalo é contado após a resposta, respeitando também a latência da rede.
          const waitMs = Math.max(0, 350 - (Date.now() - lastPatchFinishedAtRef.current));
          if (waitMs) await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
          if (!mountedRef.current || effectiveStatusRef.current !== 'active') return false;
          const changes = nextPublicChecklistPatchBatch(token, pendingChangesRef.current);
          changedFieldIds = Object.keys(changes);
          publicLogger.info('[public-checklist] patch init', { fieldsCount: changedFieldIds.length });
          Object.keys(changes).forEach((id) => delete pendingChangesRef.current[id]);
          let payload;
          try {
            payload = await patchChecklistShareValues({ token, changes });
          } catch (error) {
            // Edições feitas durante o envio têm prioridade sobre o lote que falhou.
            pendingChangesRef.current = { ...changes, ...pendingChangesRef.current };
            const message = normalizeErrorMessage(error);
            publicLogger.error('[public-checklist] patch failed', { fieldsCount: changedFieldIds.length, message });
            if (mountedRef.current) setPatchError(publicChecklistErrorMessage(error, fieldsRef.current));
            const statusCode = (error as { statusCode?: number } | null)?.statusCode;
            const transient = statusCode !== undefined ? statusCode >= 500 : /timeout|network|fetch|rede/i.test(message);
            const timedOut = (error as { code?: string } | null)?.code === 'checklist_share_timeout' || /timeout/i.test(message);
            if (transient && !timedOut && transientRetries < 2 && mountedRef.current && effectiveStatusRef.current === 'active') {
              transientRetries += 1;
              await new Promise<void>((resolve) => setTimeout(resolve, 2500 * transientRetries));
              continue;
            }
            throw error;
          } finally {
            lastPatchFinishedAtRef.current = Date.now();
          }
          if (!mountedRef.current) return false;
          publicLogger.info('[public-checklist] patch success', { fieldsCount: payload.applied_fields?.length || 0, updatesCount: payload.updates_count });
          setSessionState((prev) => prev ? {
            ...prev,
            status: payload.expires_in_seconds > 0 ? prev.status : 'expired',
            expiresAt: payload.expires_at,
            updatesCount: payload.updates_count,
            maxUpdates: payload.max_updates,
            lastActivityAt: new Date().toISOString(),
          } : prev);
          if (payload.expires_in_seconds <= 0) {
            effectiveStatusRef.current = 'expired';
            return false;
          }
        }
        if (!mountedRef.current || effectiveStatusRef.current !== 'active') return false;
        setHasPendingChanges(false);
        setPatchError(null);
        setErrorMessage(null);
        setLastSavedAt(new Date().toISOString());
        return true;
      } catch (error) {
        if (mountedRef.current) {
          const message = normalizeErrorMessage(error);
          setPatchError(publicChecklistErrorMessage(error, fieldsRef.current));
          const terminalStatus = extractTerminalStatusFromError(error) || isSessionErrorTerminal(message);
          if (terminalStatus) {
            effectiveStatusRef.current = terminalStatus;
            setSessionState((prev) => prev ? { ...prev, status: terminalStatus } : prev);
          }
          captureSentryExceptionWithScope(error, {
            level: 'warning', context: { flow: 'public_checklist', stage: 'patch', changedFieldIds },
            tags: { flow: 'public_checklist', stage: 'patch' },
            dedupeKey: `public_checklist:patch:${message}`, dedupeWindowMs: 15000,
          });
        }
        return false;
      } finally {
        // Falhas aguardam correção ou tentativa explícita, sem repetir lotes inválidos.
        clearFlushTimer();
        if (mountedRef.current) setSyncing(false);
      }
    };
    const promise = run().finally(() => { flushPromiseRef.current = null; });
    flushPromiseRef.current = promise;
    return promise;
  }, [clearFlushTimer, token]);

  const scheduleFlush = useCallback(() => {
    if (effectiveStatusRef.current !== 'active') {
      return;
    }

    clearFlushTimer();
    flushTimerRef.current = setTimeout(() => {
      void flushPendingChanges();
    }, PATCH_DEBOUNCE_MS);
  }, [clearFlushTimer, flushPendingChanges]);

  const handleCompleteSession = useCallback(async () => {
    if (!token || effectiveStatusRef.current !== 'active' || completingRef.current) {
      return;
    }

    completingRef.current = true;
    clearFlushTimer();
    setCompleting(true);
    try {
      // Garante que qualquer alteração pendente seja enviada antes de concluir.
      const saved = await flushPendingChanges();
      if (!saved || Object.keys(pendingChangesRef.current).length || effectiveStatusRef.current !== 'active') return;

      const payload = await completeChecklistShareByToken(token);
      if (!mountedRef.current) {
        return;
      }

      setSessionState((prev) =>
        prev
          ? {
              ...prev,
              status: payload.share_link.status,
              updatesCount: payload.share_link.updates_count,
              maxUpdates: payload.share_link.max_updates,
              lastActivityAt: payload.share_link.last_activity_at,
            }
          : prev
      );
      setPatchError(null);
      setErrorMessage(null);
      setLastSavedAt(new Date().toISOString());
      showToast('Alterações concluídas com sucesso.', { type: 'success', duration: 1800 });
      publicLogger.info('[public-checklist] complete success', {
        shareLinkId: payload.share_link.id,
        status: payload.share_link.status,
      });
    } catch (error) {
      const message = normalizeErrorMessage(error);
      if (!mountedRef.current) {
        return;
      }

      let terminalStatus = extractTerminalStatusFromError(error) || isSessionErrorTerminal(message);

      // Em caso de erro ambíguo no "complete", força reconciliação com o backend
      // para evitar divergir o estado local do estado real da sessão.
      if (!terminalStatus) {
        try {
          await resolveSession({ preserveForm: true, silent: true });
          if (effectiveStatusRef.current && effectiveStatusRef.current !== 'active') {
            terminalStatus = effectiveStatusRef.current;
          }
        } catch (syncError) {
          publicLogger.warn('[public-checklist] complete reconcile failed', {
            message: normalizeErrorMessage(syncError),
          });
        }
      }

      if (terminalStatus) {
        clearFlushTimer();
        pendingChangesRef.current = {};
        setSessionState((prev) =>
          prev
            ? {
                ...prev,
                status: terminalStatus,
              }
            : prev
        );
      }

      if (terminalStatus === 'completed') {
        setPatchError(null);
        setErrorMessage(null);
        showToast('Alterações concluídas com sucesso.', { type: 'success', duration: 1800 });
      } else {
        setErrorMessage(publicChecklistErrorMessage(error, fieldsRef.current));
      }

      captureSentryExceptionWithScope(error, {
        level: 'warning',
        context: {
          flow: 'public_checklist',
          stage: 'complete',
          tokenLength: token.length,
        },
        tags: {
          flow: 'public_checklist',
          stage: 'complete',
        },
        dedupeKey: `public_checklist:complete:${message}`,
        dedupeWindowMs: 20000,
      });
    } finally {
      completingRef.current = false;
      setCompleting(false);
    }
  }, [clearFlushTimer, flushPendingChanges, resolveSession, token]);

  const handleFieldChange = useCallback(
    (fieldId: string, nextValue: string) => {
      if (!isEditable || completingRef.current || !editableFieldIds.has(fieldId) || !fields.some((field) => field.id === fieldId && field.public_editable)) {
        return;
      }

      setFormValues((prev) => ({
        ...prev,
        [fieldId]: nextValue,
      }));

      pendingChangesRef.current[fieldId] = nextValue;
      setHasPendingChanges(true);
      scheduleFlush();
    },
    [editableFieldIds, fields, isEditable, scheduleFlush]
  );

  const handleSetNowTimestamp = useCallback(
    (fieldId: string) => {
      const nowIso = new Date().toISOString();
      handleFieldChange(fieldId, nowIso);
    },
    [handleFieldChange]
  );

  useEffect(() => {
    mountedRef.current = true;
    void resolveSession();

    return () => {
      mountedRef.current = false;
      clearFlushTimer();
    };
  }, [clearFlushTimer, flushPendingChanges, resolveSession]);

  useEffect(() => {
    if (!isEditable) {
      clearFlushTimer();
      return;
    }

    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [clearFlushTimer, isEditable]);

  useEffect(() => {
    if (!sessionState || !isEditable) {
      return;
    }

    let appActive = Platform.OS === 'web' || (AppState.currentState !== 'background' && AppState.currentState !== 'inactive');
    const refreshVisible = () => {
      if (appActive && (Platform.OS !== 'web' || typeof document === 'undefined' || document.visibilityState !== 'hidden')) {
        void resolveSession({ preserveForm: true, silent: true });
      }
    };
    const timer = setInterval(refreshVisible, STATUS_REFRESH_MS);
    const appState = Platform.OS === 'web' ? null : AppState.addEventListener('change', value => {
      appActive = value === 'active';
      if (appActive) refreshVisible();
    });
    if (Platform.OS === 'web' && typeof document !== 'undefined') document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      clearInterval(timer); appState?.remove();
      if (Platform.OS === 'web' && typeof document !== 'undefined') document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [isEditable, resolveSession, sessionState?.shareLinkId]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !hasPendingChanges || typeof window === 'undefined') return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [hasPendingChanges]);

  const statusBadgeStyle =
    effectiveStatus === 'active'
      ? styles.statusBadgeActive
      : effectiveStatus === 'revoked'
      ? styles.statusBadgeRevoked
      : effectiveStatus === 'expired'
      ? styles.statusBadgeExpired
      : styles.statusBadgeCompleted;

  const bottomActionLabel =
    effectiveStatus === 'completed'
      ? 'Sessão concluída'
      : effectiveStatus === 'revoked'
      ? 'Acesso encerrado'
      : effectiveStatus === 'expired'
      ? 'Link expirado'
      : 'Concluir alterações';

  const shouldShowSyncLoading = resolving || syncing;
  const syncIndicatorName =
    patchError || errorMessage
      ? 'cloud-offline-outline'
      : lastSavedAt && !hasPendingChanges
      ? 'cloud-done-outline'
      : 'cloud-outline';
  const syncIndicatorColor =
    patchError || errorMessage
      ? theme.colors.util.error
      : lastSavedAt && !hasPendingChanges
      ? theme.colors.util.success
      : theme.colors.text.secondary;
  const scrollBottomPadding = bottomActionHeight + theme.spacing.lg;

  const blockedMessage =
    effectiveStatus === 'completed'
      ? 'Alterações concluídas. Esta sessão foi encerrada com sucesso.'
      : effectiveStatus === 'revoked'
      ? 'Este acesso foi encerrado pelo motorista/admin.'
      : effectiveStatus === 'expired'
      ? 'Este link expirou. Solicite um novo QR Code ao motorista/admin.'
      : 'Esta sessão não está ativa para edição. Os campos ficaram em modo leitura.';

  const renderField = (field: ChecklistShareField) => {
    const value = formValues[field.id] ?? '';
    const canEditField = isEditable && !completing && editableFieldIds.has(field.id) && field.public_editable;

    if (!field.public_editable) {
      return (
        <View key={field.id} style={styles.readOnlyField}>
          <View style={styles.fieldHeaderRow}>
            <Text style={styles.fieldLabel}>{field.nome}</Text>
            <Ionicons name="lock-closed" size={15} color={theme.colors.text.secondary} />
          </View>
          <Text style={styles.readOnlyValue}>
            {value && value.trim().length > 0 ? value : 'Campo não disponível no modo público'}
          </Text>
        </View>
      );
    }

    if (field.tipo.startsWith('dropdown')) {
      const options = dropdownOptions[field.tipo] || [];
      return (
        <View key={field.id} style={styles.fieldContainer}>
          <Text style={styles.fieldLabel}>{field.nome}</Text>
          <View style={styles.dropdownChipsContainer}>
            {options.map((option) => {
              const selected = option === value;
              return (
                <TouchableOpacity
                  key={`${field.id}-${option}`}
                  style={[
                    styles.dropdownChip,
                    selected && styles.dropdownChipSelected,
                    !canEditField && styles.dropdownChipDisabled,
                  ]}
                  onPress={() => handleFieldChange(field.id, option)}
                  disabled={!canEditField}
                >
                  <Text
                    style={[
                      styles.dropdownChipText,
                      selected && styles.dropdownChipTextSelected,
                    ]}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {options.length === 0 ? (
            <TextInput
              style={[styles.input, !canEditField && styles.inputDisabled]}
              value={value}
              onChangeText={(text) => handleFieldChange(field.id, text)}
              editable={canEditField}
              placeholder="Digite um valor"
              placeholderTextColor={theme.colors.text.tertiary}
            />
          ) : null}
        </View>
      );
    }

    if (field.tipo === 'mensagem pronta') {
      return (
        <View key={field.id} style={styles.fieldContainer}>
          <Text style={styles.fieldLabel}>{field.nome}</Text>
          <TextInput
            style={[styles.input, styles.multilineInput, !canEditField && styles.inputDisabled]}
            value={value}
            onChangeText={(text) => handleFieldChange(field.id, text)}
            editable={canEditField}
            multiline
            numberOfLines={4}
            placeholder="Digite sua mensagem"
            placeholderTextColor={theme.colors.text.tertiary}
            textAlignVertical="top"
          />
        </View>
      );
    }

    if (field.tipo === 'timestamp with time zone') {
      return (
        <View key={field.id} style={styles.fieldContainer}>
          <View style={styles.fieldHeaderRow}>
            <Text style={styles.fieldLabel}>{field.nome}</Text>
            <TouchableOpacity
              style={[styles.nowButton, !canEditField && styles.nowButtonDisabled]}
              onPress={() => handleSetNowTimestamp(field.id)}
              disabled={!canEditField}
            >
              <Text style={styles.nowButtonText}>Usar agora</Text>
            </TouchableOpacity>
          </View>
          {canEditField ? (
            <>
              <ChecklistDateTimeField field={{ id: field.id, nome: 'Data e hora' }} value={value} onChange={(next) => handleFieldChange(field.id, next)} />
              <TouchableOpacity style={styles.nowButton} onPress={() => handleFieldChange(field.id, '')} accessibilityLabel={`Limpar ${field.nome}`}>
                <Text style={styles.nowButtonText}>Limpar</Text>
              </TouchableOpacity>
            </>
          ) : <Text style={styles.readOnlyValue}>{value ? formatDateTime(value) : 'Não informado'}</Text>}
        </View>
      );
    }

    const keyboardType = field.tipo === 'number' ? 'numeric' : 'default';
    return (
      <View key={field.id} style={styles.fieldContainer}>
        <Text style={styles.fieldLabel}>{field.nome}</Text>
        <TextInput
          style={[styles.input, !canEditField && styles.inputDisabled]}
          value={value}
          onChangeText={(text) => handleFieldChange(field.id, text)}
          editable={canEditField}
          keyboardType={keyboardType}
          placeholder={field.nome}
          placeholderTextColor={theme.colors.text.tertiary}
          autoCorrect={false}
          autoCapitalize={field.tipo === 'number' ? 'none' : 'sentences'}
        />
      </View>
    );
  };

  if (initialLoading) {
    return (
      <SafeAreaView style={styles.screen}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.centerText}>Carregando checklist público...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (errorMessage && !sessionState) {
    return (
      <SafeAreaView style={styles.screen}>
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle" size={36} color={theme.colors.util.error} />
          <Text style={styles.errorTitle}>Não foi possível abrir este checklist</Text>
          <Text style={styles.errorText}>{errorMessage}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => {
              void resolveSession();
            }}
          >
            <Text style={styles.retryButtonText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.contentContainer, { paddingBottom: scrollBottomPadding }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.headerCard}>
          <Text style={styles.companyName}>{checklistInfo.companyName || 'Empresa'}</Text>

          <View style={styles.headerTopRow}>
            <View style={styles.headerTitleBlock}>
              <Text style={styles.title}>Checklist do cliente</Text>
              <Text style={styles.subtitle}>Prencha os campos abaixo</Text>
            </View>

            <View style={styles.syncIndicatorBadge}>
              {shouldShowSyncLoading ? (
                <ActivityIndicator size="small" color={theme.colors.primary.default} />
              ) : (
                <Ionicons name={syncIndicatorName} size={18} color={syncIndicatorColor} />
              )}
            </View>
          </View>

          <View style={styles.metaRows}>
            <View style={styles.metaRow}>
              <View style={styles.metaCard}>
                <Text style={styles.metaLabel}>Número</Text>
                <Text style={styles.metaCardValue}>{checklistInfo.code || 'Sem numeração'}</Text>
              </View>

              <View style={styles.metaCard}>
                <Text style={styles.metaLabel}>Status da sessão</Text>
                <View style={[styles.statusBadge, statusBadgeStyle]}>
                  <Text style={styles.statusBadgeText}>
                    {effectiveStatus ? getStatusLabel(effectiveStatus) : 'Desconhecido'}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.metaRow}>
              <View style={styles.metaCard}>
                <Text style={styles.metaLabel}>Tempo restante</Text>
                <Text style={styles.metaCardValue}>{formatCountdown(secondsRemaining)}</Text>
              </View>

              <View style={styles.metaCard}>
                <Text style={styles.metaLabel}>Última atividade</Text>
                <Text style={styles.metaCardValue} numberOfLines={1}>
                  {formatDateTime(sessionState?.lastActivityAt || null)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {(errorMessage || patchError) && (
          <View style={styles.warningCard}>
            <View style={styles.warningMessage}>
              <Ionicons name="alert-circle-outline" size={18} color={theme.colors.util.error} />
              <Text style={styles.warningText}>{patchError || errorMessage}</Text>
            </View>
            {patchError && isEditable && (
              <TouchableOpacity style={styles.retryButton} disabled={syncing || completing} onPress={() => { void flushPendingChanges(); }}>
                <Text style={styles.retryButtonText}>Tentar salvar novamente</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {!isEditable && (
          <View style={styles.blockedCard}>
            <Ionicons name="lock-closed-outline" size={18} color={theme.colors.text.secondary} />
            <Text style={styles.blockedText}>{blockedMessage}</Text>
          </View>
        )}

        {groupedFields.map((group) => (
          <View key={group.title} style={styles.groupCard}>
            <Text style={styles.groupTitle}>{group.title}</Text>
            {group.fields.map((field) => renderField(field))}
          </View>
        ))}
      </ScrollView>

      <View
        style={[
          styles.bottomActionContainer,
          { paddingBottom: Math.max(safeAreaInsets.bottom, theme.spacing.sm) },
        ]}
        onLayout={(event) => {
          const nextHeight = Math.max(MIN_BOTTOM_ACTION_HEIGHT, Math.ceil(event.nativeEvent.layout.height));
          if (nextHeight !== bottomActionHeight) {
            setBottomActionHeight(nextHeight);
          }
        }}
      >
        <TouchableOpacity
          style={[
            styles.bottomActionButton,
            (resolving || completing || !isEditable) && styles.refreshButtonDisabled,
          ]}
          onPress={() => {
            void handleCompleteSession();
          }}
          disabled={resolving || completing || !isEditable}
        >
          {completing ? (
            <ActivityIndicator size="small" color={theme.colors.text.inverse} />
          ) : (
            <>
              <Text style={styles.bottomActionButtonText}>{bottomActionLabel}</Text>
              <Ionicons name="checkmark-circle" size={18} color={theme.colors.text.inverse} />
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  scroll: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  centerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xl,
    gap: theme.spacing.sm,
  },
  centerText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.md,
    textAlign: 'center',
  },
  errorTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.lg,
    fontWeight: '700',
    textAlign: 'center',
  },
  errorText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
  },
  retryButton: {
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    paddingVertical: 10,
    paddingHorizontal: theme.spacing.md,
    marginTop: theme.spacing.sm,
    alignSelf: 'flex-start',
  },
  retryButtonText: {
    color: theme.colors.text.inverse,
    fontWeight: '700',
    fontSize: theme.fontSizes.sm,
  },
  headerCard: {
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    gap: theme.spacing.sm,
  },
  companyName: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
  },
  headerTitleBlock: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  syncIndicatorBadge: {
    width: 36,
    height: 36,
    borderRadius: theme.borderRadius.circular,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  title: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.lg,
    fontWeight: '700',
  },
  subtitle: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  metaRows: {
    gap: theme.spacing.sm,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  metaCard: {
    flex: 1,
    minWidth: 0,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.xs,
    gap: theme.spacing.xs,
  },
  metaLabel: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
  },
  metaCardValue: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  statusBadge: {
    borderRadius: theme.borderRadius.sm,
    paddingVertical: 4,
    paddingHorizontal: theme.spacing.sm,
  },
  statusBadgeActive: {
    backgroundColor: '#E5F9EE',
  },
  statusBadgeRevoked: {
    backgroundColor: '#FDECEC',
  },
  statusBadgeExpired: {
    backgroundColor: '#FFF4E5',
  },
  statusBadgeCompleted: {
    backgroundColor: '#EEF2FF',
  },
  statusBadgeText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  warningCard: {
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#FFD2D2',
    backgroundColor: '#FFF6F6',
    padding: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  warningMessage: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing.xs,
  },
  warningText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    flex: 1,
  },
  blockedCard: {
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  blockedText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    flex: 1,
  },
  groupCard: {
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  groupTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.md,
    fontWeight: '700',
  },
  fieldContainer: {
    gap: theme.spacing.xs,
  },
  fieldHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  fieldLabel: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 10,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.background.primary,
  },
  inputDisabled: {
    backgroundColor: theme.colors.background.secondary,
    color: theme.colors.text.secondary,
  },
  multilineInput: {
    minHeight: 110,
  },
  dropdownChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  dropdownChip: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 8,
    backgroundColor: theme.colors.background.secondary,
  },
  dropdownChipSelected: {
    backgroundColor: theme.colors.primary.light,
    borderColor: theme.colors.primary.default,
  },
  dropdownChipDisabled: {
    opacity: 0.7,
  },
  dropdownChipText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  dropdownChipTextSelected: {
    color: theme.colors.primary.contrast,
  },
  readOnlyField: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.background.secondary,
    gap: theme.spacing.xs,
  },
  readOnlyValue: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  nowButton: {
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
  },
  nowButtonDisabled: {
    opacity: 0.5,
  },
  nowButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  bottomActionContainer: {
    position: 'absolute',
    left: theme.spacing.md,
    right: theme.spacing.md,
    bottom: 0,
    paddingTop: theme.spacing.sm,
    backgroundColor: 'transparent',
  },
  bottomActionButton: {
    minHeight: 56,
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.colors.primary.default,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.14,
    shadowRadius: 16,
    elevation: 8,
  },
  refreshButtonDisabled: {
    opacity: 0.65,
  },
  bottomActionButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
});

export default PublicChecklistPage;
